// useInstallPrompt — capture the `beforeinstallprompt` event at module
// load and expose a React hook + iOS fallback detector.
//
// `beforeinstallprompt` fires ONCE per page load on Chrome/Edge, and on
// some browsers it fires before the React tree has mounted. To avoid
// missing it, we register the listener at module top-level (import-time)
// and cache the event. The hook subscribes to a module-scope event-bus
// so any consumer can react when the cached event arrives or is used.
//
// iOS Safari does NOT support `beforeinstallprompt`; the user has to use
// the share-sheet's "Add to Home Screen" action. We expose a sibling
// helper, `useCanShowIosInstall`, that returns true when running in
// iOS Safari outside standalone mode — the UI layer renders an
// instructional toast / modal instead of a prompt button.

import { useEffect, useState } from 'react';

// Subset of the BeforeInstallPromptEvent we actually use. The full type
// lives in lib.dom.d.ts but isn't always present across the TS DOM
// versions we ship to; defining the structural subset keeps us strict.
interface BeforeInstallPromptEventLike extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let cachedEvent: BeforeInstallPromptEventLike | null = null;
const subscribers = new Set<() => void>();

function notify() {
  subscribers.forEach((fn) => fn());
}

if (typeof window !== 'undefined') {
  // Suppress the native mini-bar so we control the surface (Tools drawer
  // "Install on home screen" item).
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    cachedEvent = e as BeforeInstallPromptEventLike;
    notify();
  });
  // Browsers fire `appinstalled` after a successful install. Clear the
  // cache so we don't re-offer (the hook becomes `canInstall: false`).
  window.addEventListener('appinstalled', () => {
    cachedEvent = null;
    notify();
  });
}

export function useInstallPrompt(): {
  canInstall: boolean;
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
} {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    subscribers.add(fn);
    return () => {
      subscribers.delete(fn);
    };
  }, []);

  const canInstall = cachedEvent !== null;

  async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!cachedEvent) return 'unavailable';
    const evt = cachedEvent;
    // Clear before awaiting userChoice so a second tap during the prompt
    // doesn't try to prompt twice.
    cachedEvent = null;
    notify();
    await evt.prompt();
    const choice = await evt.userChoice;
    return choice.outcome;
  }

  return { canInstall, promptInstall };
}

/**
 * Returns true when the user is on iOS Safari and the app is NOT already
 * running in standalone (home-screen-installed) mode. We use this to
 * render an "Install on home screen" affordance with iOS-specific
 * instructions, since iOS lacks `beforeinstallprompt`.
 */
export function useCanShowIosInstall(): boolean {
  const [canShow, setCanShow] = useState<boolean>(() => detectIosInstall());
  useEffect(() => {
    // The result is stable per page load (UA + display-mode don't flip
    // mid-session), but we re-check on mount in case SSR returned false.
    setCanShow(detectIosInstall());
  }, []);
  return canShow;
}

function detectIosInstall(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  // `MSStream` is the IE/Edge-legacy guard the canonical iOS-UA snippet
  // recommends. The cast keeps strict mode happy without a global.d.ts.
  const w = window as unknown as { MSStream?: unknown };
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !w.MSStream;
  if (!isIos) return false;
  // Already installed → don't offer.
  const standalone =
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    // iOS-specific flag, set by Apple's home-screen webapp shell.
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  return !standalone;
}
